/**
 * LeadStageSelect
 *
 * Reusable async-paginated dropdown for selecting a lead stage.
 * Mirrors the EventSelect pattern — wraps AsyncPaginate directly with an
 * internal loader and shared AsyncSelect CSS class styling.
 *
 * Data source: getGlobalDropdown('lead_stage') from caseLeadDropdownService,
 * optionally filtered by practiceAreaId / caseId.
 * Caching:     React Query (via useQueryClient.fetchQuery) caches each page+search
 *              combination for 5 minutes. Reopening the dropdown is instant on a cache hit.
 *
 * @param {Object}       props
 * @param {Object|null}  props.value            - Selected option; must be a full option object
 *                                                (needs `sk` + `lead_stage_name`) or null.
 * @param {Function}     props.onChange          - Change handler; receives the full option object.
 * @param {string}       [props.placeholder]
 * @param {boolean}      [props.isClearable]
 * @param {boolean}      [props.isDisabled]
 * @param {string}       [props.className]
 * @param {number}       [props.pageSize]        - Records per page (default: 15).
 * @param {string}       [props.practiceAreaId]  - Filters stages to a specific practice area.
 * @param {string}       [props.caseId]          - Case context passed to the API.
 * @param {Array}        [props.cacheUniqs]      - Extra cache-busting keys (e.g. when practiceAreaId changes).
 * @param {string}       [props.name]
 * @param {string}       [props.id]
 * @param {Function}     [props.onBlur]
 * @param {Function}     [props.onFocus]
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useQueryClient } from '@tanstack/react-query';
import { fetchLeadStagesPage } from 'hooks/queries/useLeadStageDropdown';
import { queryKeys } from 'modules/cases/config/queryClient';
import '../shared/AsyncSelect.scss';

const BASE_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function LeadStageSelect({
  value = null,
  onChange,
  placeholder = 'Select',
  isClearable = false,
  isDisabled = false,
  className = '',
  pageSize = 15,
  practiceAreaId = null,
  caseId = '',
  cacheUniqs = [],
  styles: styleOverrides = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  const queryClient = useQueryClient();

  const loadOptions = useMemo(
    () => async (inputValue, _loadedOptions, additional) => {
      const page = (additional?.page ?? 0) + 1;

      // fetchQuery deduplicates in-flight requests and serves from cache when fresh.
      const result = await queryClient.fetchQuery({
        queryKey: queryKeys.leadStages.dropdown(practiceAreaId, caseId, inputValue, page, pageSize),
        queryFn: () => fetchLeadStagesPage({ practiceAreaId, caseId, search: inputValue, page, pageSize }),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      });

      return {
        options: result.options,
        hasMore: result.hasMore,
        additional: result.additional,
      };
    },
    [queryClient, practiceAreaId, caseId, pageSize]
  );

  const mergedStyles = useMemo(
    () => ({ ...BASE_STYLES, ...styleOverrides }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [styleOverrides]
  );

  const mergedCacheUniqs = useMemo(
    () => [practiceAreaId, caseId, ...cacheUniqs],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [practiceAreaId, caseId, ...cacheUniqs]
  );

  return (
    <AsyncPaginate
      name={name}
      id={id}
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      getOptionLabel={(option) => option?.lead_stage_name || option?.label || ''}
      getOptionValue={(option) => option?.sk || option?.value || ''}
      additional={{ page: 0 }}
      cacheUniqs={mergedCacheUniqs}
      debounceTimeout={300}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable
      styles={mergedStyles}
      className={`lead-stage-select ${className}`.trim()}
      classNamePrefix="async-select-custom"
      loadingMessage={() => 'Loading...'}
      noOptionsMessage={() => 'No stages found'}
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={(e) => e.target === document}
      onBlur={onBlur}
      onFocus={onFocus}
      {...rest}
    />
  );
}

LeadStageSelect.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  pageSize: PropTypes.number,
  practiceAreaId: PropTypes.string,
  caseId: PropTypes.string,
  cacheUniqs: PropTypes.array,
  styles: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default LeadStageSelect;
